import { Editor, EditorState, RichUtils, ContentState, convertFromHTML } from "draft-js";
import StyleButton from "./StyleButton";
import {
  RichEditorControls,
  RichEditorRoot,
  RichTextContainer,
} from "./RichTextControlStyles";
import { stateToHTML } from "draft-js-export-html";
import { useEffect, useRef, useState } from "react";

export interface IRichTextState {
  editorState?: EditorState;
  onToggle: any;
}

export interface IRichTextProps {
  onChange: Function;
  text?: string;
}

const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
    height: "300px",
  },
};

const getBlockStyle = (block: any) => {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    default:
      return "";
  }
};

interface DraftEditor {
  focus: any;
}

const createStateFromHtml = (html?: string) => {
  if (!html) {
    return EditorState.createEmpty();
  }
  const blocksFromHTML = convertFromHTML(html);
  const state = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );
  return EditorState.createWithContent(state);
};

const RichText: React.FC<IRichTextProps> = (props) => {
  const [editorState, setEditorState] = useState(() => createStateFromHtml(props.text));

  // The last HTML this editor reported through onChange (or was loaded with). When the text
  // prop matches it, the parent is just echoing our own edits back; anything else is an external
  // change (async load, AI rewrite, proofread fix, clearing after save) and replaces the content.
  const lastHtml = useRef(props.text ?? "");

  useEffect(() => {
    const incoming = props.text ?? "";
    if (incoming !== lastHtml.current) {
      lastHtml.current = incoming;
      setEditorState(createStateFromHtml(incoming));
    }
  }, [props.text]);

  const onChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const html = stateToHTML(newEditorState.getCurrentContent());
    lastHtml.current = html;
    props.onChange(html);
  };

  let DomEditor = {} as DraftEditor;

  const onTab = (e: React.KeyboardEvent) => {
    const maxDepth = 4;
    onChange(RichUtils.onTab(e, editorState, maxDepth));
  };

  const toggleBlockType = (blockType: string) => {
    onChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toggleInlineStyle = (inlineStyle: string) => {
    onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const setDomEditorRef = (ref: any) => {
    DomEditor = ref;
  }

  const setFocus = () => {
    DomEditor.focus();
  };

  return (
    <RichEditorRoot onClick={setFocus}>
      <BlockStyleControls
        editorState={editorState}
        onToggle={toggleBlockType}
      />
      <InlineStyleControls
        editorState={editorState}
        onToggle={toggleInlineStyle}
      />
      <RichTextContainer>
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={styleMap}
          editorState={editorState}
          onTab={onTab}
          onChange={onChange}
          ref={setDomEditorRef}
        />
      </RichTextContainer>
    </RichEditorRoot>
  );
};

const BLOCK_TYPES = [
  { label: "H1", style: "header-one" },
  { label: "H2", style: "header-two" },
  { label: "H3", style: "header-three" },
  { label: "H4", style: "header-four" },
  { label: "H5", style: "header-five" },
  { label: "H6", style: "header-six" },
  { label: "Blockquote", style: "blockquote" },
  { label: "UL", style: "unordered-list-item" },
  { label: "OL", style: "ordered-list-item" },
  { label: "Code Block", style: "code-block" },
];

const BlockStyleControls = (props: IRichTextState) => {
  const { editorState } = props;
  if (!editorState) {
    return <div />;
  }
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <RichEditorControls>
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </RichEditorControls>
  );
};

const INLINE_STYLES = [
  { label: "Bold", style: "BOLD" },
  { label: "Italic", style: "ITALIC" },
  { label: "Underline", style: "UNDERLINE" },
  { label: "Monospace", style: "CODE" },
];

const InlineStyleControls = (props: IRichTextState) => {
  if (!props.editorState) {
    return <div />;
  }
  const currentStyle = props.editorState.getCurrentInlineStyle();
  return (
    <RichEditorControls>
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </RichEditorControls>
  );
};

export default RichText;
